import { useQueryClient } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';

import { LEAD_GQL_QUERY_BASE } from 'components/domain/lead/useFetchLead';
import usePreAuthorizationReleaseMutation from 'components/domain/order/usePreAuthorizationReleaseMutation';
import useAppModal from 'hooks/useAppModal';
import { BaseErrorResponse } from 'models/_base';
import { getErrorMessage } from 'utils/axiosExceptionUtils';

const usePreAuthorizationReleaseModal = ({ leadUid }: { leadUid: string }) => {
  const { t } = useTranslation();
  const { openConfirmModal, setErrorConfirmModal } = useAppModal();
  const queryClient = useQueryClient();

  const onSuccess = () => {
    queryClient.invalidateQueries([LEAD_GQL_QUERY_BASE, leadUid]);
  };

  const onError = (error: BaseErrorResponse) => {
    setErrorConfirmModal(
      getErrorMessage({
        apiErrorCode: error?.response?.data?.apiErrorCode,
        apiErrorMessage: error?.response?.data?.apiErrorMessage ?? '',
        baseKey:
          'componentOrder.securityDepositPreAuthorization.apiErrorCodes.',
        t,
      }),
    );
    queryClient.invalidateQueries([LEAD_GQL_QUERY_BASE, leadUid]);
  };

  const { mutateAsync: releasePreAuthorization } =
    usePreAuthorizationReleaseMutation({
      onError,
      onSuccess,
    });

  const openPreAuthorizationReleaseModal = ({
    preAuthorizationUid,
  }: {
    preAuthorizationUid: string;
  }) => {
    openConfirmModal({
      body: (
        <Trans
          i18nKey="componentOrder.securityDepositPreAuthorization.releaseModal.description"
          components={{ br: <br /> }}
        />
      ),
      confirmColor: 'danger',
      confirmLabel: t(
        'componentOrder.securityDepositPreAuthorization.releaseModal.confirmButton',
      ),
      title: t(
        'componentOrder.securityDepositPreAuthorization.releaseModal.title',
      ),
      onConfirmAsync: async () => {
        await releasePreAuthorization({ preAuthorizationUid });
      },
    });
  };

  return { openPreAuthorizationReleaseModal };
};

export default usePreAuthorizationReleaseModal;
